// src/features/doctors/DoctorsPage.tsx
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, MapPin, Clock, Star, ChevronRight, Stethoscope } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { doctorService, departmentService } from '../../api/services';
import { DoctorListDto } from '../../api/types';
import clsx from 'clsx';

function SkeletonCard() {
  return (
    <div className="card p-5 space-y-3">
      <div className="flex gap-3">
        <div className="skeleton w-16 h-16 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="skeleton h-4 w-3/4" />
          <div className="skeleton h-3 w-1/2" />
          <div className="skeleton h-3 w-1/3" />
        </div>
      </div>
      <div className="skeleton h-3 w-full" />
      <div className="skeleton h-3 w-5/6" />
    </div>
  );
}

function DoctorCard({ doctor, onBook }: { doctor: DoctorListDto; onBook: (d: DoctorListDto) => void }) {
  const initials = doctor.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="card p-5 hover:shadow-card-hover transition-all duration-200 group animate-fade-up">
      <div className="flex gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {doctor.photoUrl ? (
            <img
              src={doctor.photoUrl}
              alt={doctor.name}
              className="w-16 h-16 rounded-full object-cover ring-2 ring-slate-100"
              onError={(e) => {
                const target = e.currentTarget;
                target.style.display = 'none';
                target.parentElement!.querySelector('.avatar-fallback')!.classList.remove('hidden');
              }}
            />
          ) : null}
          <div className={clsx(
            'w-16 h-16 rounded-full bg-gradient-to-br from-brand-100 to-brand-200 flex items-center justify-center text-brand-700 font-bold text-lg avatar-fallback',
            doctor.photoUrl && 'hidden'
          )}>
            {initials}
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold text-slate-900 leading-tight group-hover:text-brand-700 transition-colors">
                {doctor.title ? `${doctor.title} ` : ''}{doctor.name}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">{doctor.qualifications}</p>
            </div>

            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className={clsx(
                'text-xs px-2 py-0.5 rounded-full font-medium',
                doctor.isAcceptingBookings
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-100 text-slate-500'
              )}>
                {doctor.isAcceptingBookings ? '● Available' : '○ Unavailable'}
              </span>
              {doctor.isOnCall && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                  Subject to Availability
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-1.5">
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <MapPin className="w-3 h-3" />
              {doctor.departmentName}
            </span>
            {doctor.experience && (
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Star className="w-3 h-3" />
                {doctor.experience}
              </span>
            )}
          </div>

          {doctor.shortBio && (
            <p className="text-xs text-slate-500 mt-2 line-clamp-2 leading-relaxed">
              {doctor.shortBio}
            </p>
          )}

          {/* Schedule chips — hidden for on-call doctors */}
          {!doctor.isOnCall && doctor.schedules.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {doctor.schedules.slice(0, 4).map((s, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-100"
                >
                  <Clock className="w-2.5 h-2.5" />
                  {s.dayName.slice(0, 3)} · {s.sessionType}
                </span>
              ))}
              {doctor.schedules.length > 4 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                  +{doctor.schedules.length - 4} more
                </span>
              )}
            </div>
          )}
          {doctor.isOnCall && (
            <p className="text-xs text-amber-600 mt-3">
              On-call doctor — available on a case-by-case basis
            </p>
          )}
        </div>
      </div>

      {/* Book button */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
        {doctor.languages && (
          <span className="text-xs text-slate-400">
            🗣️ {doctor.languages}
          </span>
        )}
        <button
          onClick={() => onBook(doctor)}
          disabled={!doctor.isAcceptingBookings}
          className="btn btn-primary btn-sm ml-auto"
        >
          Book Appointment
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export function DoctorsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState<number | undefined>();
  const [onlyAvailable, setOnlyAvailable] = useState(false);

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: departmentService.getAll,
  });

  const { data: doctors = [], isLoading } = useQuery({
    queryKey: ['doctors', { departmentId, onlyAvailable }],
    queryFn: () => doctorService.getAll({
      departmentId,
      acceptingBookings: onlyAvailable || undefined,
    }),
  });

  const filtered = doctors.filter((d) =>
    !search ||
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.departmentName.toLowerCase().includes(search.toLowerCase()) ||
    d.qualifications?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Stethoscope className="w-6 h-6 text-brand-600" />
          Find a Doctor
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {filtered.length} doctor{filtered.length !== 1 ? 's' : ''} available
        </p>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              className="input pl-9"
              placeholder="Search by name, specialty…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Department filter */}
          <div className="relative min-w-[200px]">
            <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select
              className="input pl-9 appearance-none pr-8 cursor-pointer"
              value={departmentId ?? ''}
              onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Availability toggle */}
          <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
            <div
              className={clsx(
                'w-8 h-4 rounded-full transition-colors duration-200 relative',
                onlyAvailable ? 'bg-brand-600' : 'bg-slate-200'
              )}
              onClick={() => setOnlyAvailable(!onlyAvailable)}
            >
              <div
                className={clsx(
                  'absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform duration-200',
                  onlyAvailable ? 'translate-x-4' : 'translate-x-0.5'
                )}
              />
            </div>
            <span className="text-sm text-slate-600">Available only</span>
          </label>
        </div>
      </div>

      {/* Doctor grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
        ) : filtered.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <Stethoscope className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No doctors found matching your criteria</p>
            <button onClick={() => { setSearch(''); setDepartmentId(undefined); }}
              className="btn btn-ghost btn-sm mt-3">
              Clear filters
            </button>
          </div>
        ) : (
          filtered.map((doctor) => (
            <DoctorCard
              key={doctor.id}
              doctor={doctor}
              onBook={(d) => navigate(`/book?doctorId=${d.id}&departmentId=${d.id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}
